import { useState, useEffect, useCallback, useRef } from 'react';
import userService from '../services/userService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Custom hook for managing users data and operations
 * @param {Object} initialOptions - Initial configuration options
 * @returns {Object} Users state and methods
 */
export const useUsers = (initialOptions = {}) => {
  const {
    autoFetch = true,
    pageSize = 20,
    initialFilters = {}
  } = initialOptions;

  // Message alert for success notifications
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    user_type: '',
    is_active: true,
    ...initialFilters
  });

  // Sort state
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });

  const currentPageRef = useRef(currentPage);
  const filtersRef = useRef(filters);
  const sortConfigRef = useRef(sortConfig);
  const pageSizeRef = useRef(pageSize);
  const lastFetchSignatureRef = useRef(null);
  const inFlightSignatureRef = useRef(null);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    sortConfigRef.current = sortConfig;
  }, [sortConfig]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  /**
   * Fetch users with current filters and pagination
   */
  const fetchUsers = useCallback(async (options = {}) => {
    const page = options.page ?? currentPageRef.current;

    const params = {
      page,
      limit: options.limit ?? pageSizeRef.current,
      search: filtersRef.current.search,
      user_type: filtersRef.current.user_type,
      is_active: filtersRef.current.is_active,
      sort_by: sortConfigRef.current.key,
      sort_order: sortConfigRef.current.direction,
      ...options.params
    };

    const signature = JSON.stringify(params);

    if (!options.force) {
      if (inFlightSignatureRef.current === signature) {
        return;
      }

      const lastSignature = lastFetchSignatureRef.current;
      if (lastSignature?.signature === signature) {
        const elapsed = Date.now() - lastSignature.timestamp;
        if (elapsed < 200) {
          return;
        }
      }
    }

    inFlightSignatureRef.current = signature;
    setLoading(true);
    setError(null);

    try {
      const result = await userService.getUsers(params);

      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        setUsers(list);

        const totalRecords =
          result.total ?? result.pagination?.total ?? list.length;
        setTotalCount(totalRecords);

        const computedTotalPages =
          result.pagination?.total_pages ??
          Math.max(1, Math.ceil(totalRecords / (params.limit || pageSizeRef.current || 1)));
        setTotalPages(computedTotalPages);

        if (options.page && options.page !== currentPageRef.current) {
          setCurrentPage(options.page);
        }
      } else {
        setUsers([]);
        setError(result.error || i18n.t('hookErrors.users.fetchList'));
      }
    } catch (err) {
      setUsers([]);
      setError(i18n.t('hookErrors.common.unexpected'));
    } finally {
      lastFetchSignatureRef.current = { signature, timestamp: Date.now() };
      inFlightSignatureRef.current = null;
      setLoading(false);
    }
  }, []);

  /**
   * Get user by ID
   */
  const getUserById = useCallback(async (userId, options = {}) => {
    const { withLoading = false } = options;

    if (withLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await userService.getUserById(userId);

      if (result.success) {
        return result.data;
      } else {
        setError(result.error);
        return null;
      }
    } catch (err) {
      setError(i18n.t('hookErrors.users.fetchOne'));
      return null;
    } finally {
      if (withLoading) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Create new user
   */
  const createUser = useCallback(async (userData) => {
    // NOTE: don't toggle the list-level `loading` here — it drives the table
    // skeleton behind the modal and made the users list flicker on every
    // create/edit (very visible when a request FAILS and the modal stays open).
    // The page tracks its own actionLoading for the form spinner; on success we
    // refetch the list, which sets `loading` itself.
    setError(null);

    try {
      const result = await userService.createUser(userData);

      if (result.success) {
        // Refresh users list
        await fetchUsers({ force: true });

        // Show success message
        const userName = result.data?.name || result.data?.username || result.data?.email || i18n.t('users.table.copy.unnamedUser');
        showCreate(i18n.t('users.notifications.created', { name: userName }));

        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Failed to create user';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [fetchUsers, showCreate]);

  /**
   * Update user
   */
  const updateUser = useCallback(async (userId, userData) => {
    // Don't toggle list-level `loading` (see createUser note) — update patches
    // local state directly, so the table never needs a loading pass here. This
    // is what caused the users list to glitch when an edit request failed.
    setError(null);

    try {
      const result = await userService.updateUser(userId, userData);

      if (result.success) {
        // Update local state
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId ? { ...user, ...result.data } : user
          )
        );

        // Show success message
        const userName = result.data?.name || result.data?.username || result.data?.email || i18n.t('users.table.copy.unnamedUser');
        showUpdate(i18n.t('users.notifications.updated', { name: userName }));

        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Failed to update user';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate]);

  /**
   * Delete user
   */
  const deleteUser = useCallback(async (userId) => {
    // Don't toggle list-level `loading` (see createUser note) — delete patches
    // local state, so no list loading pass is needed and the table won't flicker.
    setError(null);

    try {
      // Get user details before deletion for the message
      const userToDelete = users.find(user => user.id === userId);
      const userName = userToDelete?.name || userToDelete?.username || userToDelete?.email || 'User';

      const result = await userService.deleteUser(userId);

      if (result.success) {
        // Remove from local state
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        setSelectedUsers(prev => prev.filter(id => id !== userId));
        setTotalCount(prev => prev - 1);

        // Show success message
        showDelete(i18n.t('users.notifications.deleted', { name: userName }));

        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Failed to delete user';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [users, showDelete]);

  /**
   * Bulk delete users
   */
  const bulkDeleteUsers = useCallback(async (userIds = null) => {
    const idsToDelete = userIds || selectedUsers;
    if (idsToDelete.length === 0) return { success: false, error: i18n.t('hookErrors.noSelection.users') };

    // Don't toggle list-level `loading` (see createUser note).
    setError(null);

    try {
      const result = await userService.bulkDeleteUsers(idsToDelete);

      if (result.success) {
        // Remove from local state
        setUsers(prevUsers =>
          prevUsers.filter(user => !idsToDelete.includes(user.id))
        );
        setSelectedUsers([]);
        setTotalCount(prev => prev - idsToDelete.length);

        // Show success message
        const deletedCount = idsToDelete.length;
        showDelete(`${deletedCount} user${deletedCount > 1 ? 's' : ''} ${deletedCount > 1 ? 'have' : 'has'} been deleted successfully`);

        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Failed to delete users';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [selectedUsers, showDelete]);

  /**
   * Toggle user status
   */
  const toggleUserStatus = useCallback(async (userId, isActive) => {
    setLoading(true);
    setError(null);

    try {
      const result = await userService.toggleUserStatus(userId, isActive);

      if (result.success) {
        // Update local state
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId ? { ...user, is_active: isActive } : user
          )
        );
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Failed to update user status';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Export users
   */
  const exportUsers = useCallback(async (exportFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const result = await userService.exportUsers(exportFilters);

      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = 'Failed to export users';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get labours
   */
  const getLabours = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await userService.getLabours();
      if (result.success) {
        return result.data;
      } else {
        setError(result.error);
        return [];
      }
    } catch (err) {
      setError(i18n.t('hookErrors.labours.fetchList'));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update filters and refresh data
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  /**
   * Update sort configuration
   */
  const updateSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  /**
   * Pagination methods
   */
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  }, [currentPage, totalPages]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  /**
   * Selection methods
   */
  const selectUser = useCallback((userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  }, []);

  const selectAllUsers = useCallback((shouldSelectAll = true) => {
    if (shouldSelectAll) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  }, [users]);

  const clearSelection = useCallback(() => {
    setSelectedUsers([]);
  }, []);

  const isUserSelected = useCallback((userId) => {
    return selectedUsers.includes(userId);
  }, [selectedUsers]);

  const isAllSelected = useCallback(() => {
    return users.length > 0 && selectedUsers.length === users.length;
  }, [users, selectedUsers]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (!autoFetch) return;
    fetchUsers({ page: currentPage });
  }, [autoFetch, currentPage, filters, sortConfig, fetchUsers]);

  // Return hook interface
  return {
    // Data state
    users,
    loading,
    error,
    selectedUsers,

    // Pagination state
    currentPage,
    totalPages,
    totalCount,

    // Filter and sort state
    filters,
    sortConfig,

    // Data operations
    fetchUsers,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
    bulkDeleteUsers,
    toggleUserStatus,
    exportUsers,
    getLabours,

    // Filter and sort operations
    updateFilters,
    updateSort,

    // Pagination operations
    goToPage,
    nextPage,
    prevPage,

    // Selection operations
    selectUser,
    selectAllUsers,
    clearSelection,
    isUserSelected,
    isAllSelected,

    // Computed values
    hasUsers: users.length > 0,
    hasSelection: selectedUsers.length > 0,
    selectionCount: selectedUsers.length,
  };
};